import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ThemeToggle } from "@/components/ThemeToggle";
import { useAuth } from "@/context/AuthContext";
import { AUTH } from "@/constants/testIds";
import { toast } from "sonner";
import { Sparkles, Ticket, UserPlus } from "lucide-react";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [inviteCode, setInviteCode] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const inv = params.get("invite");
    const ref = params.get("ref");
    if (inv) setInviteCode(inv.toUpperCase());
    if (ref) setReferralCode(ref.toUpperCase());
  }, [params]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await register(name, email, password, inviteCode || null, referralCode || null);
    setLoading(false);
    if (res.ok) {
      toast.success("Welcome to LaneX!");
      navigate("/app/dashboard", { replace: true });
    } else {
      setError(res.error || "Sign up failed");
    }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 md:grid-cols-2">
      <div className="relative hidden overflow-hidden border-r border-border bg-card md:flex md:flex-col md:justify-between md:p-12">
        <div className="grid-pattern grid-pattern-fade absolute inset-0 opacity-60" />
        <div className="relative">
          <Link to="/" className="flex items-center gap-2">
            <img src="/lanex-mark.png" alt="LaneX Academy" className="h-10 w-auto object-contain bg-white/95 rounded-lg p-1 shadow-md ring-1 ring-primary/30 drop-shadow-[0_0_14px_rgba(0,238,199,0.45)]" />
            <span className="font-display text-lg font-black">LaneX Academy</span>
          </Link>
        </div>
        <div className="relative max-w-md">
          <Sparkles className="mb-4 h-8 w-8 text-primary" />
          <h1 className="font-display text-4xl font-black tracking-tighter">
            Start free. <br /><span className="gradient-cyan-lime">Grow serious.</span>
          </h1>
          <p className="mt-4 text-muted-foreground">
            Your personal mentor guides you until you truly master each topic. Free forever plan.
          </p>
        </div>
        <div className="relative label-mono">// closed beta · invite-friendly</div>
      </div>

      <div className="relative flex flex-col justify-center px-6 py-12 md:px-16">
        <div className="absolute right-6 top-6"><ThemeToggle /></div>
        <div className="mx-auto w-full max-w-md">
          <div className="label-mono mb-2 text-primary">// create account</div>
          <h2 className="font-display text-3xl font-black tracking-tighter">Get started.</h2>
          <p className="mt-2 text-sm text-muted-foreground">Free forever. No credit card.</p>

          <form onSubmit={onSubmit} className="mt-8 space-y-5">
            <div>
              <Label htmlFor="name" className="label-mono">Full name</Label>
              <Input id="name" data-testid={AUTH.nameInput} required value={name} onChange={(e) => setName(e.target.value)} placeholder="Ada Lovelace" className="mt-2 h-11 rounded-lg" />
            </div>
            <div>
              <Label htmlFor="email" className="label-mono">Email</Label>
              <Input id="email" data-testid={AUTH.emailInput} type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="mt-2 h-11 rounded-lg" />
            </div>
            <div>
              <Label htmlFor="password" className="label-mono">Password</Label>
              <Input id="password" data-testid={AUTH.passwordInput} type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="min. 6 characters" className="mt-2 h-11 rounded-lg" />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="invite" className="label-mono flex items-center gap-1"><Ticket className="h-3 w-3" /> invite code</Label>
                <Input id="invite" value={inviteCode} onChange={(e) => setInviteCode(e.target.value.toUpperCase())} placeholder="optional" className="mt-2 h-11 rounded-lg font-mono" />
              </div>
              <div>
                <Label htmlFor="ref" className="label-mono flex items-center gap-1"><UserPlus className="h-3 w-3" /> referral</Label>
                <Input id="ref" value={referralCode} onChange={(e) => setReferralCode(e.target.value.toUpperCase())} placeholder="optional" className="mt-2 h-11 rounded-lg font-mono" />
              </div>
            </div>
            {error && <div data-testid={AUTH.errorText} className="rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">{error}</div>}
            <Button data-testid={AUTH.submitBtn} disabled={loading} type="submit" className="w-full h-11 rounded-full btn-neon font-mono text-xs uppercase tracking-[0.2em]">
              {loading ? "Creating…" : "Create free account"}
            </Button>
          </form>

          <div className="mt-6 text-sm text-muted-foreground">
            Already have an account?{" "}
            <Link data-testid={AUTH.switchLink} to="/login" className="text-primary hover:underline">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
